// Global "Back to home" button.
//
// Mounted once in App.tsx so every page that isn't `/` automatically gets
// a consistent way back to the home screen — no need to add it to each
// page individually. Uses `useNavigate(-1)` when there's history so it
// behaves like a real back button; otherwise falls back to `/`.
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const BackToHomeButton = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Hide on home and on a few full-bleed routes where it would overlap
  // primary chrome (auth, embedded billing return).
  const hiddenOn = ["/", "/signup"];
  if (hiddenOn.includes(location.pathname)) return null;

  const goBack = () => {
    // If we have in-app history, prefer it so users land where they came
    // from. Otherwise hard-navigate to home.
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={goBack}
      aria-label="Back to home"
      className={cn(
        "fixed z-40 font-mono text-xs gap-1.5 shadow-md backdrop-blur",
        "top-[68px] left-3 sm:top-[72px] sm:left-4",
        "bg-background/80 hover:bg-background border-border",
      )}
    >
      <ArrowLeft className="w-3.5 h-3.5" />
      Back
    </Button>
  );
};

export default BackToHomeButton;